// ============================================================================
// avecPerformanceMerger — Converte cache da Avec no formato fk_unit_performance
// ============================================================================
// Objetivo: o Dashboard e outras telas leem de `unitPerformance` (localStorage).
// Este módulo pega os dados do `useAvecSync()` e converte no MESMO formato, de
// modo que o Dashboard funciona tanto com dados da Avec quanto com dados locais.
//
// Estratégia: Avec tem PRIORIDADE sobre local. Se a Avec tem dados para (unit,
// year, month), usa Avec. Senão, fallback para o registro local.
// ============================================================================

import { AVEC_UNITS, getUnitIdByAvecKey } from '../data/avecUnitsMap.js';
import { SERVICE_ALIASES } from '../data/avecServiceAliases.js';
import { chaveRegistro } from './perfPessoaMes.js';
import { COMISSAO_UNIDADE_REAL, MES_CORRENTE_FALHA_USA_FECHAMENTO, AVEC_COLETA_ESTADO, DASH_SEM_DADO } from '../config/featureFlags.js';
import { estadoDoRelatorio, relatorioFalhou } from './avecFalhas.js';

// ─── Match serviço Avec → serviço Feel King ─────────────────────────────────
function normalizeName(name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')       // remove acentos
    .replace(/\([^)]*\)/g, '')             // remove parênteses (ex: "(keune)")
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// Pré-computa o índice: alias normalizada → svc-id
// Também guarda aliases ORDENADAS por tamanho (desc) para matching por "includes"
let _aliasIndex = null;
let _aliasSorted = null;
function buildAliasIndex() {
  if (_aliasIndex) return;
  _aliasIndex = new Map();
  const all = [];
  for (const [svcId, aliases] of Object.entries(SERVICE_ALIASES)) {
    for (const a of aliases) {
      const key = normalizeName(a);
      if (!key) continue;
      _aliasIndex.set(key, svcId);
      all.push({ key, svcId });
    }
  }
  // Ordena por tamanho desc — aliases maiores têm prioridade em match por includes
  _aliasSorted = all.sort((a, b) => b.key.length - a.key.length);
}

/**
 * Dado um nome de serviço vindo da Avec, encontra o svc-id do Feel King.
 *
 * Estratégia (em ordem):
 *   1) Match EXATO normalizado em qualquer alias
 *   2) Match por INCLUDES com a alias (maiores primeiro)
 *   3) Match por keywords em grupos semânticos (corte/barba/sobrancelha etc)
 */
function matchServiceId(avecName, feelKingServices) {
  if (!avecName) return null;
  const an = normalizeName(avecName);
  if (!an) return null;
  buildAliasIndex();

  // 1) Match exato em qualquer alias
  const direct = _aliasIndex.get(an);
  if (direct) return direct;

  // 1.5) Depilação: nariz/orelha → svc-depilacao (regra confirmada com o
  // dono); CORPORAL (máquina/costas/perna, comum no Jardins) tem id próprio —
  // entra no R$ de extras SEM contaminar a contagem gamificada de
  // nariz/orelha. Precisa vir ANTES do alias-includes ('Depilação' solto
  // mandava tudo pro svc-depilacao). Laser não passa por aqui.
  if (an.includes('depil') && !an.includes('laser')
      && !(an.includes('nariz') || an.includes('orelha'))) {
    return 'svc-depilacao-corporal';
  }

  // 2) Match por includes (aliases maiores têm prioridade)
  for (const { key, svcId } of _aliasSorted) {
    if (an.includes(key) || key.includes(an)) return svcId;
  }

  // 3) Keywords semânticas (se nenhum alias bateu)
  // Decisões por palavras-chave (composição > simples)
  const has = (...words) => words.every(w => an.includes(w));
  if (has('corte') && has('barba') && has('visagista')) return 'svc-corte-barba-visagista';
  if (has('corte') && has('barba')) return 'svc-corte-barba-completa';
  if (has('corte') && has('visagista')) return 'svc-corte-visagista';
  if (has('barba') && has('visagista')) return 'svc-barba-visagista';
  if (has('disfarce')) return 'svc-disfarce';
  // svc-depilacao SÓ pega depilação de nariz/orelha (regra Feel King).
  // Outras depilações (corporal, facial, etc — comum no Jardins) NÃO entram
  // nesse relatório — caem nos próximos matches ou ficam sem categoria.
  const hasDep = has('depilacao') || has('depilcao') || has('depillacao');
  if (hasDep && (has('nariz') || has('orelha'))) return 'svc-depilacao';
  if (has('sobrancelha')) return 'svc-sobrancelha';
  if (has('pezinho') || has('acabamento')) return 'svc-acabamento';
  // "Botox" foi renomeado pra "Desondulação (botox)" na Avec — capta ambos
  if (has('botox') || has('desondulacao')) return 'svc-botox';
  if (has('barboterapia')) return 'svc-barboterapia';
  if (has('reconstru')) return 'svc-hidratacao'; // Reconstrução Capilar (espelha o sync)
  if (has('hidratacao') && has('pe')) return 'svc-hidratacao-pes';
  if (has('hidratacao')) return 'svc-hidratacao';
  if (has('lavagem')) return 'svc-lavagem-especial';
  if (has('mascara') && has('argila')) return 'svc-mascara-argila';
  if (has('terapia') && has('facial')) return 'svc-terapia-facial';
  if (has('combo') && has('black')) return 'svc-combo-black';
  if (has('manicure') || has('pedicure')) return 'svc-manicure-pedicure';
  if (has('barba') && !has('corte')) return 'svc-barba-completa';
  if (has('corte')) return 'svc-corte';
  return null;
}

// ─── Match profissional Avec → profissional Feel King ──────────────────────
function onlyDigits(s) { return String(s || '').replace(/\D/g, ''); }
function firstName(s) { return normalizeName(s).split(' ')[0] || ''; }
// 19/10/2026 — "contido" só por PALAVRA INTEIRA. O "contido" por letras casava
// "Ana" com "Lu-ANA Giovanella": a rotina de CPF gravou o CPF da Luana no
// cadastro da Ana e o vínculo do Avec dela foi parar no cadastro errado.
// Agora todas as palavras do nome mais curto têm de aparecer inteiras no outro
// ("Ana" casa com "Ana Beatriz Pereira de Souza"; não casa com "Luana").
export function nomeContidoPorPalavra(a, b) {
  const ta = normalizeName(a).split(' ').filter(Boolean);
  const tb = normalizeName(b).split(' ').filter(Boolean);
  if (!ta.length || !tb.length) return false;
  const [curto, longo] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  const set = new Set(longo);
  return curto.every(t => set.has(t));
}


/**
 * Encontra o profissional do Feel King correspondente a um registro Avec.
 * Estratégia (em ordem de prioridade):
 *   1. CPF exato (normalizado — só dígitos)
 *   2. Apelido exato (normalizado)
 *   3. Aliases cadastrados no prof FK (ex: "Day" → ["Daiane Fabiano dos Santos"])
 *   4. Nome completo exato
 *   5. Primeiro nome Avec === primeiro nome FK (dentro da mesma unidade)
 *   6. Nome FK contido no nome completo Avec
 *
 * @param {string} avecName — nome vindo do 0021 (faturamento por profissional)
 * @param {object} avecRecord — registro completo Avec (do 0229), com apelido/cpf
 * @param {string} feelKingUnitId — unitId do FK (ex: 'unit-oscar')
 * @param {Array} feelKingProfessionals — lista de profs do FK
 * @returns {object|null} — profissional FK ou null
 */
// Vínculo DEFINITIVO Avec↔FK (fk_professionals.avec, backfill por CPF de
// 05/09/2026): resolve o id do prof na Avec SEM depender do avecListCompleta
// do snapshot nem de matching por nome — um snapshot parcial zerava a seção
// de extras em silêncio (Victor Elias, 10/09). Use SEMPRE antes do matcher.
export function avecRecFromLink(professional, unitId) {
  const l = professional?.avec?.[unitId]?.[0];
  return l?.id ? { id: l.id, nome: l.nome || '', apelido: l.apelido || '' } : null;
}

export function matchProfessionalToFeelKing(avecName, avecRecord, feelKingUnitId, feelKingProfessionals) {
  const allActive = (feelKingProfessionals || []).filter(p => p.active !== false);
  const candidates = allActive.filter(p => p.unitId === feelKingUnitId);

  // 0) Vínculo DEFINITIVO (fk_professionals.avec — backfill por CPF no 0229):
  // o id do registro Avec DESTA unidade está no vínculo do cadastro. Global
  // (rede inteira), como o CPF. Vem antes do CPF porque, desde 06/10/2026, o
  // CPF do cadastro é campo SENSÍVEL e só chega pra quem tem escopo — o
  // barbeiro não recebe o CPF dos colegas e o ranking/duelos dele casam por aqui.
  if (avecRecord?.id != null && avecRecord.id !== '') {
    const aid = String(avecRecord.id);
    const byLink = allActive.find(p => {
      const links = p?.avec?.[feelKingUnitId];
      return Array.isArray(links) && links.some(l => l && l.id != null && String(l.id) === aid);
    });
    if (byLink) return byLink;
  }

  // 0b) Nome do VÍNCULO (19/10/2026): o nome ou apelido gravado no vínculo do
  // Avec desta unidade (feito pelo CPF) é igual ao nome que veio do Avec. Cobre
  // relatório sem id (0021/0123) para quem tem nome curto no DNA ("Dan" ↔
  // "Danilo Macedo"), sem depender do "contido".
  {
    const an0 = normalizeName(avecName);
    if (an0) {
      const byLinkName = allActive.find(p => {
        const links = p?.avec?.[feelKingUnitId];
        return Array.isArray(links) && links.some(l => l && (normalizeName(l.nome) === an0 || (l.apelido && normalizeName(l.apelido) === an0 && normalizeName(l.apelido).includes(' '))));
      });
      if (byLinkName) return byLinkName;
    }
  }

  // 1) CPF match GLOBAL (rede inteira, ignora unidade) — fonte de verdade
  // Cruza CPF do Avec com CPF cadastrado no Feel King independente da unidade.
  // Resolve casos onde o prof transferiu de unidade ou foi cadastrado em outra.
  if (avecRecord?.cpf) {
    const avecCpf = onlyDigits(avecRecord.cpf);
    if (avecCpf.length >= 11) {
      const byCpf = allActive.find(p => onlyDigits(p.cpf || '') === avecCpf);
      if (byCpf) return byCpf;
    }
  }

  // Demais estratégias requerem prof na MESMA unidade (mais conservador)
  if (candidates.length === 0) return null;

  // 2) Apelido match
  if (avecRecord?.apelido) {
    const ap = normalizeName(avecRecord.apelido);
    if (ap) {
      const byApelido = candidates.find(p => normalizeName(p.name) === ap);
      if (byApelido) return byApelido;
    }
  }

  // 3) Aliases cadastrados no prof FK
  // Resolve apelidos curtos: prof "Day" tem alias "Daiane Fabiano dos Santos"
  // → bate com o nome do Avec mesmo sem apelido oficial.
  const an = normalizeName(avecName);
  const byAlias = candidates.find(p =>
    Array.isArray(p.aliases) && p.aliases.some(a => normalizeName(a) === an)
  );
  if (byAlias) return byAlias;

  // 4) Nome completo exato (dos profs FK → Avec)
  const byFullName = candidates.find(p => normalizeName(p.name) === an);
  if (byFullName) return byFullName;

  // 4) Primeiro nome Avec === primeiro nome FK
  const avecFirst = firstName(avecName);
  if (avecFirst) {
    const byFirst = candidates.filter(p => firstName(p.name) === avecFirst);
    if (byFirst.length === 1) return byFirst[0];
    // Se tiver mais de 1, tenta desambiguar por cargo
    if (byFirst.length > 1 && avecRecord?.cargo) {
      const byRole = byFirst.find(p => normalizeName(p.role) === normalizeName(avecRecord.cargo));
      if (byRole) return byRole;
    }
  }

  // 5) Nome FK contido no Avec ou vice-versa, por PALAVRA INTEIRA (19/10/2026)
  for (const p of candidates) {
    if (nomeContidoPorPalavra(p.name, avecName)) return p;
  }

  return null;
}

/**
 * Escaneia o cache da Avec e descobre CPFs novos pra cadastrar nos profs FK.
 * Como o CPF é a chave de matching mais confiável, alimentar ele uma vez
 * elimina erros futuros (ex: Day = "Daiane Fabiano dos Santos") sem depender
 * de aliases manuais.
 *
 * @param {object} avecCache — cache.byMonth[YYYY-MM] = {units:{avecKey: unitData}}
 * @param {object} avecSnapshots — snapshots[YYYY-MM] = {avecKey: unitData} (opcional)
 * @param {Array} professionals — lista FK
 * @returns {Array<{profId, cpf, source}>} — CPFs descobertos (sem duplicar profId)
 */
function casamentoForteParaCpf(p, a, unitId) {
  const links = p?.avec?.[unitId];
  if (a?.id != null && Array.isArray(links) && links.some(l => l && String(l.id) === String(a.id))) return true;
  const pn = normalizeName(p?.name);
  if (pn && (pn === normalizeName(a?.nome) || (a?.apelido && pn === normalizeName(a.apelido)))) return true;
  const an = normalizeName(a?.nome);
  return Array.isArray(p?.aliases) && p.aliases.some(x => normalizeName(x) === an);
}

export function discoverProfessionalCpfs(avecCache, avecSnapshots, professionals) {
  const found = new Map(); // profId → cpf (último ganha — mas só popula se FK não tiver)
  const profIndex = new Map();
  for (const p of (professionals || [])) {
    if (p?.id) profIndex.set(p.id, p);
  }

  const scanUnits = (unitsObj) => {
    if (!unitsObj) return;
    for (const [avecKey, unitData] of Object.entries(unitsObj)) {
      if (!unitData || unitData._error) continue;
      const feelKingUnitId = getUnitIdByAvecKey(avecKey);
      if (!feelKingUnitId) continue;
      const lista = unitData.profissionais?.avecListCompleta || [];
      for (const a of lista) {
        const cpf = onlyDigits(a?.cpf || '');
        if (cpf.length < 11) continue;
        // Tenta casar o registro Avec com prof FK por nome/apelido/alias
        const matched = matchProfessionalToFeelKing(
          a.nome || '',
          a,
          feelKingUnitId,
          professionals,
        );
        if (!matched) continue;
        const currentCpf = onlyDigits(matched.cpf || '');
        // 19/10/2026: só PREENCHE CPF vazio, nunca troca um CPF já gravado (a
        // troca silenciosa pôs o CPF da Luana no cadastro da Ana). E só com
        // casamento FORTE: vínculo do Avec, nome completo, apelido ou alias
        // iguais; primeiro nome e "contido" não bastam para gravar CPF.
        if (currentCpf) continue;
        if (!casamentoForteParaCpf(matched, a, feelKingUnitId)) continue;
        found.set(matched.id, cpf);
      }
    }
  };

  // Cache por mês: { 'YYYY-MM': { units: { avecKey: unitData } } }
  for (const monthData of Object.values(avecCache || {})) {
    if (monthData && typeof monthData === 'object') {
      scanUnits(monthData.units || monthData);
    }
  }
  // Snapshots: { 'YYYY-MM': { avecKey: unitData } }
  for (const monthData of Object.values(avecSnapshots || {})) {
    scanUnits(monthData);
  }

  return Array.from(found.entries()).map(([profId, cpf]) => ({ profId, cpf }));
}

/**
 * Detecta se uma linha de serviço Avec é o COMBO de depilação Nariz+Orelha.
 */
function isDepilacaoCombo(name) {
  const n = normalizeName(name);
  if (!n) return false;
  const isDep = n.includes('depilacao') || n.includes('depilcao') || n.includes('depillacao');
  return isDep && n.includes('nariz') && n.includes('orelha');
}
function isDepilacaoSimplesNariz(name) {
  const n = normalizeName(name);
  if (!n) return false;
  const isDep = n.includes('depilacao') || n.includes('depilcao') || n.includes('depillacao');
  return isDep && n.includes('nariz') && !n.includes('orelha');
}
function isDepilacaoSimplesOrelha(name) {
  const n = normalizeName(name);
  if (!n) return false;
  const isDep = n.includes('depilacao') || n.includes('depilcao') || n.includes('depillacao');
  return isDep && n.includes('orelha') && !n.includes('nariz');
}

/**
 * Converte extras da Avec (array de {nome, qtd}) em objeto {svc-id: qtd}
 * no formato esperado pelo sistema Feel King.
 *
 * Regra Feel King para depilações (CONFIRMADA com o cliente):
 *   - "Depilação de Nariz"          → vale 1 cada (mult = 1)
 *   - "Depilação de Orelha"         → vale 1 cada (mult = 1)
 *   - "Depilação de Nariz e Orelha" → vale 2 cada (mult = 2)
 *
 *   Avec SEMPRE retorna as 3 linhas como INDEPENDENTES (combo NÃO está
 *   embutido em nariz/orelha simples). Confirmado tanto no relatório por
 *   profissional (0024) quanto no agregado por unidade (0032).
 *
 *   Exemplo Henrique Moema (mai/26):
 *     Nariz=5, Orelha=3, Combo=4 → 5×1 + 3×1 + 4×2 = 16
 */
export function avecExtrasToServicosExtras(avecExtras, feelKingServices) {
  const list = avecExtras || [];
  const map = {};
  for (const e of list) {
    const svcId = matchServiceId(e.nome, feelKingServices);
    if (!svcId) continue;
    const mult = isDepilacaoCombo(e.nome) ? 2 : 1;
    map[svcId] = (map[svcId] || 0) + ((Number(e.qtd) || 0) * mult);
  }
  return map;
}

// Serviços que NÃO contam como "extra" no faturamento (regra do dono,
// 07/08/2026): corte/barba e as variações VISAGISTA são serviço principal, não
// extra. Espelha o SKIP_SERVICES do avec-daily-sync.mjs — as duas pontas
// (histórico do sync e mês corrente ao vivo) precisam usar a mesma régua,
// senão o KPI muda de valor quando o mês fecha.
const NAO_EXTRA = [
  'corte', 'corte de cabelo e barba', 'corte de cabelo barba completa',
  'barba', 'barba completa', 'acabamento de cabelo', 'acabamento cabelo',
  'combo black', 'corte visagista', 'corte e barba visagista', 'barba visagista',
  'corte a maquina', 'consultoria visagista',
  // Pézinho = acabamento (12/08/2026) — espelha o SKIP_SERVICES do sync;
  // sem isso o "você fez hoje" somava pézinho e o mês oficial não.
  'pezinho',
];

function isNaoExtra(nome) {
  const s = normalizeName(nome); // minúsculo, sem acento/pontuação
  if (!s) return true;
  return NAO_EXTRA.some(k => s === k || s.startsWith(k + ' '));
}

/**
 * R$ faturado em SERVIÇOS EXTRAS (07/08/2026 — dashboard individual passou a
 * mostrar o valor, não só a quantidade). Soma o `faturamento` que o 0024 já
 * devolve por serviço, contando SÓ o que casa com um extra do catálogo E não
 * está na lista de não-extras. Diferente da quantidade, aqui NÃO se dobra
 * combo de depilação: R$ é R$, o combo já vem com o valor cheio.
 */
export function avecExtrasToValor(avecExtras, feelKingServices) {
  let total = 0;
  for (const e of (avecExtras || [])) {
    if (isNaoExtra(e.nome)) continue;
    if (!matchServiceId(e.nome, feelKingServices)) continue;
    total += Number(e.faturamento) || 0;
  }
  return Math.round(total * 100) / 100;
}

/**
 * R$ de PERFORMANCE por consumo de pacote (12/08/2026): linhas do 0031 com
 * valor 0 (cliente pagou na venda do pacote) valem preço de TABELA × 0,8
 * (pacote tem 20% off — regra do dono) pro barbeiro que EXECUTOU. Não é
 * caixa: entra só na meta diária e nos duelos.
 */
export function avecExtrasPacoteToValor(rows, feelKingServices, tabelaPrecos) {
  if (!rows?.length || !tabelaPrecos) return 0;
  const tabela = {};
  for (const [nome, valor] of Object.entries(tabelaPrecos)) {
    tabela[normalizeName(nome)] = Number(valor) || 0;
  }
  let total = 0;
  for (const r of rows) {
    const nome = r.servico ?? r.nome;
    if (isNaoExtra(nome)) continue;
    if (!matchServiceId(nome, feelKingServices)) continue;
    // RATEIO REAL (valor_pacote ÷ sessões, vindo do 0064×0063) quando o
    // range trouxe; tabela × 0,8 é aproximação de fallback (caso Kayque
    // 22/08/2026: pacote montado a R$1200/5=R$240 vs tabela 185×0,8=148).
    const sessao = Number(r.valorSessao) || 0;
    if (sessao > 0) { total += sessao; continue; }
    const cheio = tabela[normalizeName(nome)] || 0;
    if (cheio > 0) total += cheio * 0.8;
  }
  return Math.round(total * 100) / 100;
}

/**
 * Converte pacotes da Avec (array de {nome, qtd}) em objeto {pkg-id: qtd}.
 * Faz matching de nome (normalizado) entre nomes Avec e pacotes Feel King.
 * Quando não encontra ID match, agrupa sob a chave do nome normalizado
 * (assim totais ainda contam mesmo sem mapeamento).
 */
export function avecPacotesToPacotesVendidos(avecPacotes, feelKingPackages) {
  const map = {};
  for (const e of (avecPacotes || [])) {
    const pkgId = matchPackageId(e.nome, feelKingPackages);
    const key = pkgId || `_unmatched_${normalizeName(e.nome)}`;
    map[key] = (map[key] || 0) + (Number(e.qtd) || 0);
  }
  return map;
}

// Match por nome com fuzzy (igual a matchServiceId)
function matchPackageId(avecName, feelKingPackages) {
  if (!avecName || !feelKingPackages?.length) return null;
  const target = normalizeName(avecName);
  if (!target) return null;
  // Exato
  let m = feelKingPackages.find(p => normalizeName(p.name) === target);
  if (m) return m.id;
  // Contém
  m = feelKingPackages.find(p => {
    const pn = normalizeName(p.name);
    return pn && (target.includes(pn) || pn.includes(target));
  });
  return m?.id || null;
}

/**
 * Converte um objeto de dados Avec (uma unidade, um mês) em registro
 * compatível com `unitPerformance` (formato que o Dashboard espera).
 *
 * @param feelKingServices — lista de serviços do FK (para mapear extras)
 */
// A5 (23/10/2026, H7): comissão da unidade = soma real do 0123 (a pagar +
// adiantamentos, a mesma régua do cartão ao vivo), nunca 40% do faturamento.
//   0123 falhou → null; lista com linhas → soma em centavos; lista vazia com
//   o 0123 respondido (vazio/ok) → 0 real; lista vazia em cache antigo (sem
//   _coleta, não dá para saber) → null. Quem lê trata null como "Sem dado".
export function comissaoRealDaUnidade(avecData) {
  if (!avecData) return null;
  if (relatorioFalhou(avecData, '0123')) return null;
  const lista = avecData.profissionais?.comissoes;
  if (Array.isArray(lista) && lista.length > 0) {
    return Math.round(lista.reduce((a, p) => a + (Number(p?.comissao) || 0), 0) * 100) / 100;
  }
  const est = estadoDoRelatorio(avecData, '0123');
  return (est === 'vazio' || est === 'ok' || est === 'herdado') ? 0 : null;
}

// Revisão 23/10/2026 (achados 8 e 15): A5 e A5b põem null em campo da unidade,
// e só a tela nova (DASH_SEM_DADO) trata null como "Sem dado" (o sum() do
// Dashboard antigo, o gráfico de 12 meses e "Todos da unidade" fazem `|| 0` e
// mostrariam R$ 0,00 ou a rede sem a unidade, sem aviso). Por isso a chave
// EFETIVA de cada uma exige DASH_SEM_DADO ligada; sozinhas = merger de antes.
export const COMISSAO_UNIDADE_REAL_EFETIVA = COMISSAO_UNIDADE_REAL && DASH_SEM_DADO;
export const MES_CORRENTE_FALHA_USA_FECHAMENTO_EFETIVA = MES_CORRENTE_FALHA_USA_FECHAMENTO && AVEC_COLETA_ESTADO && DASH_SEM_DADO;

export function avecUnitDataToPerformanceRecord(avecKey, year, month, avecData, feelKingServices = [], opts = {}) {
  const feelKingUnitId = getUnitIdByAvecKey(avecKey);
  if (!feelKingUnitId || !avecData) return null;
  const comissaoReal = opts.comissaoReal ?? COMISSAO_UNIDADE_REAL_EFETIVA;

  // Comissão estimada via 40% do faturamento (regra atual do sistema)
  // 23/10/2026: com COMISSAO_UNIDADE_REAL, a soma real do 0123 (ver acima).
  const comissao = comissaoReal ? comissaoRealDaUnidade(avecData) : (avecData.faturamento || 0) * 0.4;

  // Converte extras (0032) em servicosExtras {svc-id: qtd}
  const servicosExtras = avecExtrasToServicosExtras(avecData.extras, feelKingServices);
  // R$ em extras da unidade (0032 tem o faturamento por serviço). Mesma régua
  // dos profissionais: visagismo fora. 0 aqui → o merge cai no valor já
  // sincronizado (ver mergeUnitPerformanceWithAvec).
  const valorServicosExtras = avecExtrasToValor(
    (avecData.extras || []).map(e => ({ nome: e.nome, faturamento: e.faturamento ?? e.valor })),
    feelKingServices
  );

  // Preferenciais / sem preferência: contagem BRUTA do 0326 (colunas
  // preferencial e semPreferencia por profissional), somada na unidade.
  // (Antes recomputava comandas × percentual — o correto é o 0326 direto.)
  const agendList = avecData.profissionais?.agendamentosPorProfissional || [];
  let totalPref = 0;
  let totalSemPref = 0;
  for (const a of agendList) {
    totalPref += Number(a.preferencial) || 0;
    totalSemPref += Number(a.semPreferencia) || 0;
  }

  return {
    id: `uperf-${feelKingUnitId}-${year}-${month}`,
    unitId: feelKingUnitId,
    year,
    month,
    // Campos essenciais
    faturamento: avecData.faturamento || 0,
    clientesAtendidos: avecData.visitas || 0, // regra: visitas é o nº de atendimentos
    clientesUnicos: avecData.clientesUnicos || 0,
    clientesNovos: avecData.clientesNovos || 0,
    servicosRealizados: avecData.servicosRealizados || 0,
    produtosVendidos: avecData.produtosVendidos || 0,
    faturamentoProdutos: avecData.faturamentoProdutos || 0,
    agendamentosPreferenciais: totalPref,
    semPreferencia: totalSemPref,
    // Geração de demanda da UNIDADE = clientes novos + sem preferência
    // (definição do dono, 04/08/2026). Por profissional é só o sem preferência
    // (não há "clientes novos" por pessoa) — ver linha ~636.
    geracaoDemanda: totalSemPref + (avecData.clientesNovos || 0),
    pacotesVendidosTotal: avecData.pacotesVendidosTotal || 0,
    valorPacotes: avecData.valorPacotes || 0,  // R$ faturado em pacotes na unidade (0062)
    valorServicosExtras,                        // R$ em serviços extras (sem visagismo)
    taxaRetorno: avecData.taxaRetorno || 0,
    ticketMedio: avecData.ticketMedio || 0,
    tracaoVendas: avecData.tracaoVendas || 0,
    comissao,
    servicosExtras,
    // Sinalização que veio da Avec
    _fromAvec: true,
    _fetchedAt: avecData._fetchedAt,
  };
}

/**
 * Mescla `localUnitPerformance` com dados do `avecCache`/`avecSnapshots`.
 * Avec tem prioridade — se existe registro na Avec para (unit, year, month),
 * substitui o local. Senão, mantém o local.
 *
 * @param {Array} localUnitPerformance — array original do fk_unit_performance
 * @param {Object} avecCache — conteúdo do `fk_avec_cache` (formato { 'YYYY-MM': {units: {...}} })
 * @param {Object} avecSnapshots — conteúdo do `fk_avec_snapshots`
 * @returns {Array} — novo array de unitPerformance mesclado
 */
// A5b (MES_CORRENTE_FALHA_USA_FECHAMENTO, só junto de AVEC_COLETA_ESTADO): no
// mês corrente, campo cujo relatório está em _falhas não entra como 0 do cache:
// fica null e cai no registro local do fechamento (fk_unit_performance, 08:30 e
// 20:30); sem registro local continua null. Desligado = o merger de antes.
const CAMPOS_REC_POR_RELATORIO = [
  ['faturamento', ['0281']],
  ['clientesAtendidos', ['0089']],
  ['clientesUnicos', ['0002']],
  ['clientesNovos', ['0012']],
  ['servicosRealizados', ['0031']],
  ['produtosVendidos', ['0041']],
  ['faturamentoProdutos', ['0041']],
  ['agendamentosPreferenciais', ['0326']],
  ['semPreferencia', ['0326']],
  ['geracaoDemanda', ['0326', '0012']],
  ['pacotesVendidosTotal', ['0062']],
  ['valorPacotes', ['0062']],
];
export function aplicarFalhasNoRegistroDoMes(rec, unitData, local) {
  if (!rec || !unitData || !Array.isArray(unitData._falhas) || unitData._falhas.length === 0) return rec;
  const falhou = (c) => unitData._falhas.includes(c);
  const out = { ...rec };
  let mexeu = false;
  for (const [campo, codigos] of CAMPOS_REC_POR_RELATORIO) {
    const semDado = codigos.some(falhou) || (campo === 'clientesAtendidos' && unitData.visitas == null);
    if (!semDado) continue;
    const l = local?.[campo];
    out[campo] = (l !== undefined && l !== null) ? Number(l) || 0 : null;
    mexeu = true;
  }
  if (out.comissao == null && local?.comissao != null) { out.comissao = Number(local.comissao) || 0; mexeu = true; }
  if (!mexeu) return rec;
  const fat = out.faturamento, cli = out.clientesAtendidos, svc = out.servicosRealizados;
  out.ticketMedio = (fat == null || cli == null) ? null : (cli > 0 ? fat / cli : 0);
  out.tracaoVendas = (svc == null || cli == null) ? null : (cli > 0 ? svc / cli : 0);
  return out;
}

export function mergeUnitPerformanceWithAvec(localUnitPerformance, avecCache, avecSnapshots, feelKingServices = [], opts = {}) {
  const avecRecords = [];
  const usaFechamento = opts.mesCorrenteUsaFechamento ?? MES_CORRENTE_FALHA_USA_FECHAMENTO_EFETIVA;
  const comissaoReal = opts.comissaoReal ?? COMISSAO_UNIDADE_REAL_EFETIVA;

  // Index dos registros LOCAIS por (unitId, year, month) — p/ fallback de servicosExtras
  const localIndex = new Map();
  for (const r of (localUnitPerformance || [])) {
    localIndex.set(`${r.unitId}|${r.year}|${r.month}`, r);
  }

  // Mês FECHADO (< mês corrente): o FECHAMENTO oficial do cloud (daily-sync,
  // que desde 04/08/2026 re-sela o mês anterior nos dias 1–5) VENCE sobre o
  // cache/snapshot Avec deste device. Motivo: o cache local de um mês fechado
  // é a última visita DURANTE o mês (parcial — sem o último dia) e ficava
  // sobrescrevendo o fechamento pra sempre → tração/clientes/serviços de julho
  // "bagunçados" no Dashboard (04/08/2026). Cache só preenche mês SEM registro.
  const nowMerge = new Date();
  const isClosedMonth = (y, m) =>
    y < nowMerge.getFullYear() || (y === nowMerge.getFullYear() && m < nowMerge.getMonth() + 1);
  const localHasData = (key) => {
    const l = localIndex.get(key);
    return !!l && ((Number(l.faturamento) || 0) > 0 || (Number(l.clientesAtendidos) || 0) > 0);
  };

  const processMonth = (monthKey, monthData, isSnapshot = false) => {
    if (!monthData) return;
    const [year, month] = monthKey.split('-').map(Number);
    const units = isSnapshot ? monthData : (monthData.units || {});
    for (const [avecKey, unitData] of Object.entries(units)) {
      if (!unitData || unitData._error) continue;
      // Ignora resposta vazia da API (falha silenciosa ou token sem acesso)
      // para não sobrescrever dados locais bons com zeros
      if ((unitData.faturamento || 0) === 0 && (unitData.visitas || 0) === 0) continue;
      const rec = avecUnitDataToPerformanceRecord(avecKey, year, month, unitData, feelKingServices, { comissaoReal });
      if (rec && isClosedMonth(year, month) && localHasData(`${rec.unitId}|${year}|${month}`)) {
        // HÍBRIDO (mês fechado): fechamento oficial do cloud vence nos
        // indicadores, mas herda do cache o que o registro do cloud não tem
        // (servicosExtras de fechamentos antigos, clientesUnicos, taxaRetorno).
        // updatedAt fica o do cloud → no upload o empate mantém o cloud
        // (mergeFn é ts(l) > ts(c)) — é só visual, por device.
        const local = localIndex.get(`${rec.unitId}|${year}|${month}`);
        const hybrid = { ...local };
        if ((!hybrid.servicosExtras || Object.keys(hybrid.servicosExtras).length === 0) &&
            rec.servicosExtras && Object.keys(rec.servicosExtras).length > 0) {
          hybrid.servicosExtras = rec.servicosExtras;
        }
        if (!(Number(hybrid.clientesUnicos) > 0)) hybrid.clientesUnicos = rec.clientesUnicos;
        if (!(Number(hybrid.taxaRetorno) > 0)) hybrid.taxaRetorno = rec.taxaRetorno;
        if (!(Number(hybrid.valorServicosExtras) > 0)) hybrid.valorServicosExtras = rec.valorServicosExtras;
        avecRecords.push(hybrid);
        continue;
      }
      if (rec) {
        // Se o Avec não populou servicosExtras (ou populou vazio) MAS o local tem,
        // preserva o local para não perder visualização
        const hasAvecExtras = rec.servicosExtras && Object.keys(rec.servicosExtras).length > 0;
        const local = localIndex.get(`${rec.unitId}|${year}|${month}`);
        if (!hasAvecExtras) {
          if (local?.servicosExtras && Object.keys(local.servicosExtras).length > 0) {
            rec.servicosExtras = local.servicosExtras;
          }
        }
        // R$ de extras: o SINCRONIZADO vence o cálculo ao vivo. O sync usa o
        // 0031 item-a-item com a régua curada (EXTRAS_MAP + SKIP, visagismo
        // fora); o cálculo ao vivo casa pelo catálogo e pega serviço demais —
        // na rede deu R$72,7k ao vivo contra R$43,8k do sync (07/08/2026), e o
        // número despencaria ao fechar o mês. Ao vivo só preenche buraco.
        if (local?.valorServicosExtras !== undefined) {
          rec.valorServicosExtras = Number(local.valorServicosExtras) || 0;
        }
        if (usaFechamento) { avecRecords.push(aplicarFalhasNoRegistroDoMes(rec, unitData, local)); continue; }
        if (comissaoReal && rec.comissao == null && local?.comissao != null) rec.comissao = Number(local.comissao) || 0;
        avecRecords.push(rec);
      }
    }
  };

  // Snapshots são autoritativos para meses fechados — processa primeiro.
  const snapshotMonths = new Set(Object.keys(avecSnapshots || {}));
  for (const [monthKey, monthData] of Object.entries(avecSnapshots || {})) {
    processMonth(monthKey, monthData, true);
  }
  // Cache só para meses NÃO cobertos por snapshot (evita duplicar quando ambos existem).
  for (const [monthKey, monthData] of Object.entries(avecCache || {})) {
    if (snapshotMonths.has(monthKey)) continue;
    processMonth(monthKey, monthData, false);
  }

  // Dedup defensivo dentro do próprio avecRecords (mesma chave unit|year|month vence o 1º)
  const seenAvec = new Set();
  const avecIndex = new Set();
  const merged = [];
  for (const r of avecRecords) {
    const key = `${r.unitId}|${r.year}|${r.month}`;
    if (seenAvec.has(key)) continue;
    seenAvec.add(key);
    avecIndex.add(key);
    merged.push(r);
  }

  // Adiciona os locais que NÃO foram sobrescritos pela Avec
  for (const r of (localUnitPerformance || [])) {
    if (avecIndex.has(`${r.unitId}|${r.year}|${r.month}`)) continue;
    merged.push(r);
  }

  return merged;
}

/**
 * Similar, mas para `performance` (por profissional).
 * Usa o array `profissionais.faturamento` do cache Avec.
 *
 * @param feelKingPackages — opcional. Necessário pra mapear pacotesVendidos
 *                          como objeto {pkgId: qty} (caso recepcionista).
 */
export function mergePerformanceWithAvec(localPerformance, avecCache, avecSnapshots, professionals, feelKingServices = [], feelKingPackages = []) {
  const avecRecords = [];

  // Index local por (profId|unitId|year|month) — usado pra HERDAR campos que a Avec
  // não traz (metaMensal, metaTicketMedio, metaClientesPreferenciais, observacoes...)
  // Sem isso, prof com dados Avec perde a meta cadastrada manualmente.
  // 26/10/2026 (Kayk, Moema → Oscar em outubro): a chave tem a UNIDADE
  // (perfPessoaMes.chaveRegistro). A pessoa com número em duas unidades no mês
  // tem um registro em cada; sem a unidade, o da Moema herdava o piso de
  // comissão e os bloqueios da Oscar (e vice-versa).
  const localProfIndex = new Map();
  for (const r of (localPerformance || [])) {
    if (r.professionalId && r.year && r.month) {
      localProfIndex.set(chaveRegistro(r), r);
    }
  }
  const chaveLocal = (profId, unitId, year, month) =>
    chaveRegistro({ professionalId: profId, unitId, year, month });

  // Mês FECHADO: o fechamento oficial do cloud vence sobre o cache Avec deste
  // device (mesma regra do merge de unidade — ver comentário lá; 04/08/2026).
  const nowMergeProf = new Date();
  const isClosedMonthProf = (y, m) =>
    y < nowMergeProf.getFullYear() || (y === nowMergeProf.getFullYear() && m < nowMergeProf.getMonth() + 1);
  const localProfHasData = (key) => {
    const l = localProfIndex.get(key);
    return !!l && ((Number(l.faturamento) || 0) > 0 || (Number(l.comissao) || 0) > 0 || (Number(l.clientesAtendidos) || 0) > 0);
  };

  const processMonth = (monthKey, monthData, isSnapshot = false) => {
    if (!monthData) return;
    const [year, month] = monthKey.split('-').map(Number);
    const units = isSnapshot ? monthData : (monthData.units || {});

    for (const [avecKey, unitData] of Object.entries(units)) {
      if (!unitData || unitData._error) continue;
      const feelKingUnitId = getUnitIdByAvecKey(avecKey);
      if (!feelKingUnitId) continue;

      const profFaturamento = unitData.profissionais?.faturamento || [];
      const profComissoes = unitData.profissionais?.comissoes || [];
      const profProdutos = unitData.profissionais?.produtos || [];
      const profPacotes = unitData.profissionais?.pacotes || [];
      const extrasPorProf = unitData.profissionais?.extrasPorProfissional || {};
      const pacotesPorProf = unitData.profissionais?.pacotesPorProfissional || {};
      const agendPorProf = unitData.profissionais?.agendamentosPorProfissional || [];
      // Comandas de PACOTE/ASSINATURA por prof (do 0064 — key = apelido).
      // Usado pra ajustar ticket médio: cliente de assinatura entra como
      // atendido mas NÃO conta no divisor do ticket (não gerou fat na comanda).
      const comandasPacotePorProf = unitData.profissionais?.comandasPacotePorProf || {};

      // Lista completa da Avec dessa unidade (para match por apelido/CPF)
      const avecListCompleta = unitData.profissionais?.avecListCompleta || [];

      // Pra recepcionistas: cria registros pra TODOS os profs com pacotes vendidos
      // (não só os que estão em profFaturamento, pois recepcionistas geralmente não
      // aparecem no 0021 — só atendentes).
      const allProfNames = new Set(profFaturamento.map(p => p.nome));
      for (const nome of Object.keys(pacotesPorProf)) {
        if (!allProfNames.has(nome) && pacotesPorProf[nome]?.length > 0) {
          // Adiciona um "pseudo" registro com apenas pacotes (sem faturamento próprio)
          profFaturamento.push({ nome, faturamento: 0, comandas: 0, servicos: 0, ticketMedio: 0, tracaoVendas: 0 });
        }
      }

      for (const pf of profFaturamento) {
        // Encontra o registro Avec do prof pelo nome (do 0021)
        const avecRecord = avecListCompleta.find(
          a => normalizeName(a.nome) === normalizeName(pf.nome) ||
               normalizeName(a.apelido) === normalizeName(pf.nome)
        );

        // Match inteligente com Feel King (camadas de prioridade)
        const feelKingProf = matchProfessionalToFeelKing(
          pf.nome,
          avecRecord,
          feelKingUnitId,
          professionals || []
        );
        if (!feelKingProf) continue;
        // Mês fechado com registro oficial no cloud → os INDICADORES oficiais
        // (faturamento, comissão, clientes...) vêm do fechamento. Mas NÃO dá
        // pra pular o registro inteiro: o fechamento (daily-sync) não grava
        // servicosExtras/pacotesVendidos/valorPacotes — esses só existem no
        // cache/snapshot ao vivo. Pular zerava "serviços extras individuais"
        // de todo mês fechado (07/08/2026). Ver push HÍBRIDO no fim do loop.
        const closedOfficial = isClosedMonthProf(year, month) && localProfHasData(chaveLocal(feelKingProf.id, feelKingUnitId, year, month));

        const prod = profProdutos.find(p => p.nome?.toLowerCase() === pf.nome?.toLowerCase());
        // Casa a COMISSÃO (relatório 0123) com o FATURAMENTO (0021) pelo ID do
        // profissional na Avec — robusto contra grafia diferente do nome entre
        // os dois relatórios (era a causa da comissão zerar SÓ pra quem tinha
        // nome divergente, ex: Matheus). Cai pro nome se faltar o id.
        const com = (pf.profissionalId != null &&
            profComissoes.find(p => p.profissionalId != null && String(p.profissionalId) === String(pf.profissionalId)))
          || profComissoes.find(p => p.nome?.toLowerCase() === pf.nome?.toLowerCase());
        const pac = profPacotes.find(p => p.nome?.toLowerCase() === pf.nome?.toLowerCase());

        // Extras do profissional (0024) → servicosExtras {svc-id: qtd} + R$
        const extrasArr = extrasPorProf[pf.nome] || [];
        const servicosExtras = avecExtrasToServicosExtras(
          extrasArr.map(e => ({ nome: e.servico, qtd: e.qtd })),
          feelKingServices
        );
        const valorServicosExtras = avecExtrasToValor(
          extrasArr.map(e => ({ nome: e.servico, faturamento: e.faturamento })),
          feelKingServices
        );

        // Pacotes do profissional → pacotesVendidos {pkg-id: qty}
        // PRIORIDADE: 0065 (pacotesPorProfissional) — relatório oficial por prof.
        // FALLBACK 1: 0225 (pac.porPacote) — ranking de pacotes (menos preciso).
        // FALLBACK 2: pac.pacotesVendidos como total avulso.
        const pkgFromReport0065 = pacotesPorProf[pf.nome];
        let pacotesVendidos;
        let valorPacotes = 0; // R$ faturado pelo prof — vem direto do 0065 (campo 'valor')
        if (Array.isArray(pkgFromReport0065) && pkgFromReport0065.length > 0) {
          pacotesVendidos = avecPacotesToPacotesVendidos(pkgFromReport0065, feelKingPackages);
          valorPacotes = pkgFromReport0065.reduce((s, p) => s + (Number(p.valor) || 0), 0);
        } else if (pac?.porPacote) {
          pacotesVendidos = avecPacotesToPacotesVendidos(pac.porPacote, feelKingPackages);
          valorPacotes = (pac.porPacote || []).reduce((s, p) => s + (Number(p.valor) || 0), 0);
        } else if (pac?.pacotesVendidos > 0) {
          pacotesVendidos = { _total: pac.pacotesVendidos };
        } else {
          pacotesVendidos = {};
        }
        // Total para indicador rápido (soma dos values)
        const pacotesTotal = Object.values(pacotesVendidos).reduce((s, v) => s + (Number(v) || 0), 0);

        // Agendamentos preferenciais/sem preferência do 0326 (por apelido):
        // usa a contagem BRUTA das colunas preferencial e semPreferencia.
        // Match por apelido — aceita nome completo OU primeiro nome de qualquer
        // lado. Antes faltava extrair firstName do apelido Avec, então caso
        // como apelido='Victor Elias' vs nome FK='Victor' não casava.
        const agend = agendPorProf.find(a => {
          const apA = normalizeName(a.apelido);
          const apF = firstName(a.apelido);
          const pfFull = normalizeName(pf.nome);
          const pfFirst = firstName(pf.nome);
          const fkFull = normalizeName(feelKingProf.name);
          const fkFirst = firstName(feelKingProf.name);
          return apA === pfFull || apA === fkFull ||
                 apA === pfFirst || apA === fkFirst ||
                 apF === pfFirst || apF === fkFirst;
        }) || {};
        const prefCount = Number(agend.preferencial) || 0;
        const semPrefCount = Number(agend.semPreferencia) || 0;

        // Herda metas e observações do registro local (se houver) — Avec NÃO
        // traz esses campos. Sem isso, meta diária e cards de meta ficam zerados.
        const localRec = localProfIndex.get(chaveLocal(feelKingProf.id, feelKingUnitId, year, month));

        // Ajusta ticket médio excluindo comandas de pacote/assinatura.
        // O `comandasPacotePorProf` é keyed por APELIDO (campo `apelido` do 0064).
        // O nome do 0021 (pf.nome) é o nome completo. Cruzamos por avecListCompleta
        // (que tem ambos) pra achar o apelido correto e somar.
        const apelidoMatch = avecListCompleta.find(a =>
          normalizeName(a.nome) === normalizeName(pf.nome)
        )?.apelido;
        // Tenta vários formatos pq 0064 retorna apelido cru (pode ter case diff)
        const comandasPacote = (
          comandasPacotePorProf[apelidoMatch] ||
          comandasPacotePorProf[apelidoMatch?.trim()] ||
          comandasPacotePorProf[pf.nome] ||
          0
        );
        const fat = pf.faturamento || 0;
        const totalComandas = pf.comandas || 0;
        const comandasPagas = Math.max(totalComandas - comandasPacote, 0);
        // "Ticket sem pacote" (fat ÷ comandas pagas) vira campo AUXILIAR.
        // Ele não pode ser o ticketMedio exibido: em prof com muito pacote o
        // denominador despenca e o ticket explode (Victor/Oscar mai: 57
        // comandas, 20 de pacote → R$662 em vez de R$430). Pior: o campo
        // comandasPacotePorProf só existe em caches/snapshots novos, então
        // cada device via um número diferente (device recém-instalado via o
        // inflado; device antigo via o cru).
        const ticketMedioAjustado = comandasPagas > 0 ? fat / comandasPagas : 0;

        if (closedOfficial) {
          // HÍBRIDO (mês fechado): base = fechamento oficial do cloud
          // (localRec); do cache ao vivo herda APENAS o que o daily-sync não
          // grava. Id perf-* mantém o registro fora do upload (filtro
          // anti-fantasma do supabase.js) — é por device e nunca reescreve o
          // cloud; os oficiais continuam intocados.
          const l = localRec;
          avecRecords.push({
            ...l,
            id: `perf-${feelKingProf.id}-${year}-${month}`,
            comandasPacote: Number(l.comandasPacote) > 0 ? l.comandasPacote : comandasPacote,
            ticketMedioSemPacote: Number(l.ticketMedioSemPacote) > 0 ? l.ticketMedioSemPacote : ticketMedioAjustado,
            faturamentoProdutos: l.faturamentoProdutos !== undefined ? (Number(l.faturamentoProdutos) || 0) : (prod?.faturamentoProdutos || 0),
            pacotesVendidos: (l.pacotesVendidos && Object.keys(l.pacotesVendidos).length > 0) ? l.pacotesVendidos : pacotesVendidos,
            pacotesVendidosTotal: Number(l.pacotesVendidosTotal) > 0 ? l.pacotesVendidosTotal : (pacotesTotal || (pac?.pacotesVendidos || 0)),
            valorPacotes: Number(l.valorPacotes) > 0 ? l.valorPacotes : valorPacotes,
            servicosExtras: (l.servicosExtras && Object.keys(l.servicosExtras).length > 0) ? l.servicosExtras : servicosExtras,
            valorServicosExtras: l.valorServicosExtras !== undefined ? (Number(l.valorServicosExtras) || 0) : valorServicosExtras,
            agendamentosPreferenciais: l.agendamentosPreferenciais ?? prefCount,
            semPreferencia: l.semPreferencia ?? l.agendamentosSemPreferencia ?? semPrefCount,
            geracaoDemanda: l.geracaoDemanda ?? l.agendamentosSemPreferencia ?? semPrefCount,
            _fromAvec: true,
          });
          continue;
        }

        avecRecords.push({
          id: `perf-${feelKingProf.id}-${year}-${month}`,
          professionalId: feelKingProf.id,
          unitId: feelKingUnitId,
          year,
          month,
          faturamento: fat,
          clientesAtendidos: totalComandas, // regra: comandas = clientes atendidos do prof (inclui assinaturas)
          comandasPacote, // qtd de comandas que foram só pacote (sem fat na comanda)
          servicosRealizados: pf.servicos || 0,
          // Ticket exibido = CRU do Avec (fat ÷ comandas): determinístico,
          // igual em todo device e consistente com snapshot/fk_performance.
          ticketMedio: pf.ticketMedio || (totalComandas > 0 ? fat / totalComandas : 0),
          ticketMedioSemPacote: ticketMedioAjustado, // auxiliar p/ análise
          tracaoVendas: pf.tracaoVendas || 0,
          // MAX(cache ao vivo, sync noturno): resposta parcial da API (0123
          // vazio) zerava a comissão da unidade inteira no app enquanto o
          // cloud tinha o valor certo (caso Moema ago/2026). Comissão só
          // CRESCE dentro do mês — o sincronizado é o piso.
          comissao: Math.max(Number(com?.comissao) || 0, Number(localRec?.comissao) || 0),
          produtosVendidos: prod?.qtdProdutos || 0,
          // R$ de produtos no MÊS ABERTO: MAX(sincronizado, ao vivo). O sync
          // noturno só grava às 20:30 — se o sincronizado sempre vencesse, o
          // ranking/acumulado ficava PARADO o dia inteiro (bug 12/08/2026).
          // MAX é seguro pq o valor só cresce dentro do mês e as duas pontas
          // usam a MESMA régua (0041 sem bebidas) desde 12/08. Resposta
          // parcial da API ao vivo não derruba (max segura o sincronizado).
          faturamentoProdutos: Math.max(
            Number(localRec?.faturamentoProdutos) || 0,
            Number(prod?.faturamentoProdutos) || 0
          ),
          pacotesVendidos,
          pacotesVendidosTotal: pacotesTotal || (pac?.pacotesVendidos || 0),
          valorPacotes, // R$ faturado em pacotes pelo prof (do 0065)
          servicosExtras,
          // R$ em serviços extras no MÊS ABERTO: MAX(sincronizado, ao vivo).
          // Antes o sincronizado sempre vencia e o ranking/acumulado ficava
          // PARADO até o sync das 20:30 (bug 12/08/2026) — as CONTAGENS
          // (servicosExtras acima) andavam ao vivo mas o R$ não. As duas
          // pontas usam a mesma régua curada desde 12/08 (radicais botox/
          // depilação, pézinho fora, visagismo fora), então o max nunca trava
          // sobrecontagem. Mês fechado nem passa por aqui (vai pelo híbrido,
          // fechamento oficial intacto).
          valorServicosExtras: Math.max(
            Number(localRec?.valorServicosExtras) || 0,
            Number(valorServicosExtras) || 0
          ),
          // Consumo de pacote executado (tabela×0,8) — vem do sync noturno;
          // o cache mensal (0024 agregado) não separa pago de pacote.
          valorExtrasPacote: Number(localRec?.valorExtrasPacote) || 0,
          // Bloqueios de agenda do mês (0159) — só o sync noturno grava
          bloqueiosAgenda: Number(localRec?.bloqueiosAgenda) || 0,
          minutosBloqueadosAgenda: Number(localRec?.minutosBloqueadosAgenda) || 0,
          agendamentosPreferenciais: prefCount,
          semPreferencia: semPrefCount,
          geracaoDemanda: semPrefCount, // do profissional
          // Metas + obs vêm do local (cadastradas manualmente pelo admin)
          metaMensal: localRec?.metaMensal || 0,
          metaTicketMedio: localRec?.metaTicketMedio || 0,
          metaClientesPreferenciais: localRec?.metaClientesPreferenciais || 0,
          observacoes: localRec?.observacoes || '',
          _fromAvec: true,
        });
      }
    }
  };

  // Snapshots primeiro (autoritativo p/ meses fechados); cache só para meses não cobertos.
  const snapshotMonths = new Set(Object.keys(avecSnapshots || {}));
  for (const [monthKey, monthData] of Object.entries(avecSnapshots || {})) {
    processMonth(monthKey, monthData, true);
  }
  for (const [monthKey, monthData] of Object.entries(avecCache || {})) {
    if (snapshotMonths.has(monthKey)) continue;
    processMonth(monthKey, monthData, false);
  }

  // Dedup defensivo (mesma chave prof|unidade|year|month vence o 1º).
  // 26/10/2026: com a UNIDADE na chave. Antes o registro ao vivo da Oscar do
  // Kayk derrubava o da Moema do mesmo mês (e o guardado também), e o mês dele
  // aparecia pela metade. O número da pessoa é a soma (perfPessoaMes.perfDoMes);
  // o da unidade é o registro dela.
  const seenAvec = new Set();
  const idsUsados = new Set();
  const merged = [];
  for (const r of avecRecords) {
    const key = chaveRegistro(r);
    if (seenAvec.has(key)) continue;
    seenAvec.add(key);
    // id 'perf-' único: a 2ª unidade da pessoa no mês ganha a unidade no id
    // (sem 2ª unidade, o id de sempre).
    let rec = r;
    if (idsUsados.has(rec.id)) rec = { ...rec, id: `perf-${rec.professionalId}-${rec.unitId}-${rec.year}-${rec.month}` };
    idsUsados.add(rec.id);
    merged.push(rec);
  }
  for (const r of (localPerformance || [])) {
    if (seenAvec.has(chaveRegistro(r))) continue;
    merged.push(r);
  }
  return merged;
}
